import { AbortError } from '@frontend/controllers';
import { isPromiseLike } from '@frontend/utils';

const enum FullfilledState {
  unfullfilled = 0,
  resolved = 1,
  rejected = 2,
}

type Resolver<T> = (value: T | PromiseLike<T>) => void;
//eslint-disable-next-line @typescript-eslint/no-explicit-any
type Rejector = (reason?: any) => void;

export class WaitUntilSetController<T> {
  #waitUntilSet: Promise<T>;
  #resolve?: Resolver<T>;
  #reject?: Rejector;
  #fullfilledState: FullfilledState = FullfilledState.unfullfilled;

  constructor(abortSignal?: AbortSignal) {
    this.#waitUntilSet = new Promise<T>((resolve, reject) => {
      this.#resolve = resolve;
      this.#reject = reject;
    });

    let onAbort: undefined | (() => void);

    if (abortSignal) {
      abortSignal.addEventListener(
        'abort',
        (onAbort = () => {
          this.setReject(new AbortError('Aborted'));
        }),
      );
    }

    //In case this.set() is called with Promise as value, we have to wait for that promise.
    this.#waitUntilSet.then(
      () => {
        this.#fullfilledState = FullfilledState.resolved;
        this.#resolve = this.#reject = undefined;
        if (abortSignal && onAbort) {
          abortSignal.removeEventListener('abort', onAbort);
        }
        return Promise.resolve();
      },
      () => {
        this.#fullfilledState = FullfilledState.rejected;
        this.#resolve = this.#reject = undefined;
        if (abortSignal && onAbort) {
          abortSignal.removeEventListener('abort', onAbort);
        }
        return Promise.resolve();
      },
    );
  }

  public get waitUntilSet(): Promise<T> {
    return this.#waitUntilSet;
  }

  public get isFullfilled(): boolean {
    return this.#fullfilledState !== FullfilledState.unfullfilled;
  }

  public get isUnFullfilled(): boolean {
    return this.#fullfilledState === FullfilledState.unfullfilled;
  }

  public get isResolved(): boolean {
    return this.#fullfilledState === FullfilledState.resolved;
  }

  public get isRejected(): boolean {
    return this.#fullfilledState === FullfilledState.rejected;
  }

  set(value: T | PromiseLike<T>) {
    if (this.#resolve) {
      this.#resolve(value);
      if (!isPromiseLike(value)) {
        this.#fullfilledState = FullfilledState.resolved;
      } //otherwise wait until value resolves
      this.#resolve = this.#reject = undefined;
    }
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  setReject(reason?: any) {
    if (this.#reject) {
      this.#reject(reason);
      this.#fullfilledState = FullfilledState.rejected;
      this.#resolve = this.#reject = undefined;
    }
  }
}
